/**
 * Typed fetch client to the Worker `/api/*` surface.
 *
 * In production Cloudflare Access injects the auth header on every request,
 * so calls from the browser are unauthenticated from the SPA's perspective —
 * the Worker reads the email header and resolves the staff row.
 *
 * In dev, set `VITE_DEV_ADMIN_TOKEN` in `.env.local` to short-circuit auth
 * via the bearer fallback (matches `ADMIN_SYNC_TOKEN` on the Worker).
 */

class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function call<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const devToken = (import.meta as unknown as ImportMetaWithEnv).env?.VITE_DEV_ADMIN_TOKEN;
  if (devToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${devToken}`);
  }
  const res = await fetch(`/api${path}`, { ...init, headers });
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }
  if (!res.ok) {
    throw new ApiError(res.status, parsed, `${init.method ?? "GET"} ${path} failed`);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: "POST", body: body == null ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: "PUT", body: body == null ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: "PATCH", body: body == null ? undefined : JSON.stringify(body) }),
};

interface ImportMetaWithEnv {
  env?: { VITE_DEV_ADMIN_TOKEN?: string };
}

export { ApiError };
